import { useCallback } from 'react';

import { SetURLSearchParams, useSearchParams } from 'react-router-dom';

import { PRESERVED_PARAMS } from '../constants/preservedParams';

/**
 * Wrapper around useSearchParams that automatically preserves special params
 * like 'upcoming-features' when updating search params.
 */
export const usePreservedSearchParams = (): [URLSearchParams, SetURLSearchParams] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setPreservedSearchParams: SetURLSearchParams = useCallback(
    (nextInit, navigateOptions) => {
      setSearchParams((prev) => {
        // Get the new params
        const newParams = typeof nextInit === 'function' ?
          nextInit(prev) :
          new URLSearchParams(nextInit as string | URLSearchParams | string[][] | Record<string, string>);

        // Preserve special params from current URL OR sessionStorage if not in new params
        PRESERVED_PARAMS.forEach((param) => {
          if (!newParams.has(param)) {
            // First check current URL
            let value = prev.get(param);
            // If not in URL, check sessionStorage
            if (!value) {
              value = sessionStorage.getItem(`preserved_${param}`);
            }
            if (value) {
              newParams.set(param, value);
            }
          }
        });

        return newParams;
      }, navigateOptions);
    },
    [setSearchParams],
  );

  return [searchParams, setPreservedSearchParams];
};
